import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Lock, Shield, Trash2, AlertTriangle, Eye, EyeOff, UserCog, KeyRound, HelpCircle, ExternalLink } from 'lucide-react';
import { ProfileAPI, PersonalAccessTokenAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useCanAccess } from '../hooks/useCanAccess';
import { ApiKeysPanel } from '../components/ApiKeysPanel';

const GITHUB_PAT_PATTERN = /^(ghp_[A-Za-z0-9]{36}|github_pat_[A-Za-z0-9_]{22,})$/;

export function Settings() {
  const { logout, user } = useAuth();
  // Owner/Admin on a plan that includes API access -- hidden entirely otherwise (API plans are not shown in the UI).
  const canSeeApiKeys = useCanAccess('apikeys', 'read');
  // `code` is set once at signup (`GITHUB-...` vs `PROF-...`) and never changes,
  // unlike `repoprovider`, which local-login users can also acquire by connecting
  // a GitHub repo/PAT for scanning -- that field indicates repo access, not signup method.
  const isGithubUser = !!user?.code?.startsWith('GITHUB-');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [patValue, setPatValue] = useState('');
  const [showPat, setShowPat] = useState(false);
  const [patError, setPatError] = useState<string | null>(null);
  const [patSuccess, setPatSuccess] = useState(false);
  const [isSavingPat, setIsSavingPat] = useState(false);
  const [connectedUsername, setConnectedUsername] = useState<string | null>(null);
  const [showPatHelpDialog, setShowPatHelpDialog] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await PersonalAccessTokenAPI.get();
        if (response.data?.connected) {
          setConnectedUsername(response.data.username ?? null);
        }
      } catch {
        // Silently ignore - the PAT tab simply shows as not connected
      }
    })();
  }, []);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setUpdateError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8 || passwordData.newPassword.length > 30) {
      setUpdateError('Password must be between 8 and 30 characters long.');
      return;
    }

    if (/[^A-Za-z\d@$!%*?&]/.test(passwordData.newPassword)) {
      setUpdateError('Password can only contain letters, numbers, and the special characters @$!%*?&');
      return;
    }

    if (!/(?=.*[a-z])/.test(passwordData.newPassword)) {
      setUpdateError('Password must contain at least one lowercase letter');
      return;
    }

    if (!/(?=.*[A-Z])/.test(passwordData.newPassword)) {
      setUpdateError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/(?=.*\d)/.test(passwordData.newPassword)) {
      setUpdateError('Password must contain at least one number');
      return;
    }

    if (!/(?=.*[@$!%*?&])/.test(passwordData.newPassword)) {
      setUpdateError('Password must contain at least one special character (@$!%*?&)');
      return;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      await ProfileAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setIsUpdating(false);
      setUpdateSuccess(true);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (error) {
      setIsUpdating(false);
      setUpdateError(error instanceof Error ? error.message : 'Failed to update password');
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);

    try {
      await ProfileAPI.deleteAccount();

      logout();
      toast.success('Account deletion initiated successfully.');
    } catch (error) {
      setIsDeleting(false);
      toast.error('Failed to delete account. Please try again or contact support.');
    }
  };

  const togglePasswordVisibility = (field: string) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field as keyof typeof prev] }));
  };

  const handleSavePat = async (e: React.FormEvent) => {
    e.preventDefault();
    setPatSuccess(false);

    if (!GITHUB_PAT_PATTERN.test(patValue)) {
      setPatError('Enter a valid GitHub token (starts with "ghp_" or "github_pat_")');
      return;
    }

    setPatError(null);
    setIsSavingPat(true);

    try {
      const response = await PersonalAccessTokenAPI.save(patValue);
      setIsSavingPat(false);
      setPatSuccess(true);
      setPatValue('');
      setConnectedUsername(response.data?.username ?? null);
      setTimeout(() => setPatSuccess(false), 3000);
    } catch (error) {
      // Error is already surfaced via the global toast in apiClient - no inline alert needed here.
      setIsSavingPat(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Settings</h1>
          <p className="text-muted-foreground">Manage your account and preferences</p>
        </div>

        <Tabs defaultValue={isGithubUser ? "pat" : "account"}>
          <TabsList>
            {!isGithubUser && (
              <TabsTrigger
                value="account"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground"
              >
                <UserCog className="w-4 h-4 mr-1.5" />
                Account
              </TabsTrigger>
            )}
            <TabsTrigger
              value="pat"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground"
            >
              <KeyRound className="w-4 h-4 mr-1.5" />
              Personal Access Token
            </TabsTrigger>
            {canSeeApiKeys && (
              <TabsTrigger
                value="apikeys"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground"
              >
                <KeyRound className="w-4 h-4 mr-1.5" />
                API Keys
              </TabsTrigger>
            )}
            <TabsTrigger
              value="danger"
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground dark:data-[state=active]:bg-primary dark:data-[state=active]:text-primary-foreground"
            >
              <AlertTriangle className="w-4 h-4 mr-1.5" />
              Danger Zone
            </TabsTrigger>
          </TabsList>

          {!isGithubUser && (
          <TabsContent value="account">
            <Card className="bg-card shadow-sm rounded-lg border-0">
              <CardHeader className="pb-6">
                <div className="flex items-center space-x-2">
                  <Lock className="w-5 h-5 text-primary" />
                  <CardTitle>Change Password</CardTitle>
                </div>
                <CardDescription>
                  Update your password to keep your account secure
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {updateSuccess && (
                  <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                    <Shield className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-800 dark:text-green-200">
                      Password updated successfully!
                    </AlertDescription>
                  </Alert>
                )}

                {updateError && (
                  <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                    <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <AlertDescription className="text-red-800 dark:text-red-200">
                      {updateError}
                    </AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) => {
                          setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }));
                          setUpdateError(null);
                        }}
                        placeholder="Enter current password"
                        className="pr-10 h-12 rounded-lg border-border focus:border-primary focus:ring-primary"
                        required
                        maxLength={100}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) => {
                          setPasswordData(prev => ({ ...prev, newPassword: e.target.value }));
                          setUpdateError(null);
                        }}
                        placeholder="Enter new password"
                        className="pr-10 h-12 rounded-lg border-border focus:border-primary focus:ring-primary"
                        required
                        maxLength={100}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('new')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Must be 8+ characters with uppercase, lowercase, a number, and a special character (@$!%*?&)
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) => {
                          setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }));
                          setUpdateError(null);
                        }}
                        placeholder="Confirm new password"
                        className="pr-10 h-12 rounded-lg border-border focus:border-primary focus:ring-primary"
                        required
                        maxLength={100}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('confirm')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isUpdating}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
                  >
                    {isUpdating ? 'Updating...' : 'Update Password'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
          )}

          <TabsContent value="pat">
            <Card className="bg-card shadow-sm rounded-lg border-0">
              <CardHeader className="pb-6">
                <div className="flex items-center space-x-2">
                  <KeyRound className="w-5 h-5 text-primary" />
                  <CardTitle>Personal Access Token</CardTitle>
                </div>
                <CardDescription>
                  Connect your GitHub account so ORbiT8 can scan your repositories.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {patSuccess && (
                  <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950">
                    <Shield className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <AlertDescription className="text-green-800 dark:text-green-200">
                      GitHub token saved successfully!
                    </AlertDescription>
                  </Alert>
                )}

                {patError && (
                  <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                    <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <AlertDescription className="text-red-800 dark:text-red-200">
                      {patError}
                    </AlertDescription>
                  </Alert>
                )}

                {connectedUsername && (
                  <p className="text-sm text-muted-foreground">
                    Connected as <span className="font-medium text-foreground">{connectedUsername}</span>
                  </p>
                )}

                <form onSubmit={handleSavePat} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="githubPat">GitHub personal access token</Label>
                    <div className="relative">
                      <Input
                        id="githubPat"
                        type={showPat ? "text" : "password"}
                        value={patValue}
                        onChange={(e) => {
                          setPatValue(e.target.value);
                          setPatError(null);
                        }}
                        placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                        className="pr-10 h-12 rounded-lg border-border focus:border-primary focus:ring-primary font-mono"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPat(prev => !prev)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPat ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Requires the repo and read:org scopes. Create one in{' '}
                      <a
                        href="https://github.com/settings/tokens"
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary underline"
                      >
                        GitHub developer settings
                      </a>.
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      type="submit"
                      disabled={isSavingPat}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-6"
                    >
                      {isSavingPat ? (connectedUsername ? 'Updating...' : 'Saving...') : (connectedUsername ? 'Update Token' : 'Save Token')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-lg hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors"
                      onClick={() => setShowPatHelpDialog(true)}
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      How to create a PAT
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Dialog open={showPatHelpDialog} onOpenChange={setShowPatHelpDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-primary" />
                    GitHub Personal Access Token (PAT)
                  </DialogTitle>
                  <DialogDescription>
                    A GitHub Personal Access Token is required only to scan repositories.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  <div className="rounded-lg bg-muted p-4 space-y-3">
                    <div>
                      <p className="font-semibold text-sm">Where to keep it in the tool</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        In <span className="font-medium text-foreground">Settings → Personal Access Token</span>, you can save a token so it's automatically used for every new GitHub scan.
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="font-semibold text-sm mb-2">Option A — Fine-grained token (recommended)</p>
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      <li>Go to GitHub → Settings → Developer settings → Personal access tokens → <span className="font-medium text-foreground">Fine-grained tokens</span>.</li>
                      <li>Click Generate new token.</li>
                      <li>Under Repository permissions, set Contents to <span className="font-medium text-foreground">Read-only</span>.</li>
                      <li>Copy the token and paste it into Orbit8's GitHub PAT input field.</li>
                    </ol>
                  </div>

                  <div>
                    <p className="font-semibold text-sm mb-2">Option B — Classic token</p>
                    <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                      <li>Go to GitHub → Settings → Developer settings → Personal access tokens → <span className="font-medium text-foreground">Tokens (classic)</span>.</li>
                      <li>Click Generate new token (classic).</li>
                      <li>Grant the repo scope (read access to private repositories).</li>
                      <li>Copy the token and paste it into Orbit8's GitHub PAT input field.</li>
                    </ol>
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-lg hover:border-primary hover:text-primary hover:bg-primary/10 transition-colors"
                    onClick={() => window.open('https://github.com/settings/tokens', '_blank')}
                  >
                    Open GitHub tokens
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    type="button"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg"
                    onClick={() => setShowPatHelpDialog(false)}
                  >
                    Got it
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {canSeeApiKeys && (
            <TabsContent value="apikeys">
              <ApiKeysPanel />
            </TabsContent>
          )}

          <TabsContent value="danger">
            <Card className="bg-card shadow-sm rounded-lg border-0 border-red-200 dark:border-red-800">
              <CardHeader className="pb-6">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 dark:text-red-400" />
                  <CardTitle className="text-red-700 dark:text-red-400">Danger Zone</CardTitle>
                </div>
                <CardDescription>
                  Irreversible and destructive actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                    <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                    <AlertDescription className="text-red-800 dark:text-red-200">
                      Account deletion is permanent and cannot be undone. All your data, projects, and scans will be permanently deleted.
                    </AlertDescription>
                  </Alert>

                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-red-700 dark:text-red-400">Delete Account</h4>
                      <p className="text-sm text-muted-foreground">Permanently delete your account and all associated data</p>
                    </div>

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="destructive"
                          className="rounded-lg"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Account
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-popover rounded-lg">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-red-700 dark:text-red-400">
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your account
                            and remove all your data from our servers, including:
                            <ul className="list-disc list-inside mt-2 space-y-1">
                              <li>All projects and scan results</li>
                              <li>Team members and permissions</li>
                              <li>Reports and historical data</li>
                              <li>Billing information and invoices</li>
                            </ul>
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDeleteAccount}
                            disabled={isDeleting}
                            className="bg-destructive hover:bg-destructive/90 rounded-lg"
                          >
                            {isDeleting ? 'Deleting...' : 'Yes, delete my account'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
